import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './TopBar.module.css';

function ConfirmModal({ message, onConfirm, onCancel }) {
  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <p className={styles.modalMsg}>{message}</p>
        <div className={styles.modalActions}>
          <button className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button className={styles.confirmBtn} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TopBar({ publishStatus, onPublish, onUnpublish, isOwner, isEditMode, onEdit, onDone }) {
  const [confirm, setConfirm] = useState(null); // null | 'publish' | 'draft'
  const [statusOpen, setStatusOpen] = useState(false);
  const menuRef = useRef(null);
  const isPublished = publishStatus === 1;

  useEffect(() => {
    if (!statusOpen) return;
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [statusOpen]);

  const handleStatusChange = (nextStatus) => {
    setStatusOpen(false);
    if (nextStatus === 'published' && !isPublished) setConfirm('publish');
    if (nextStatus === 'draft' && isPublished) setConfirm('draft');
  };

  const handleConfirm = () => {
    if (confirm === 'publish') onPublish();
    if (confirm === 'draft') onUnpublish();
    setConfirm(null);
  };

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.left}>
          {isOwner ? (
            <div className={styles.statusMenuWrap} ref={menuRef}>
              <button
                className={`${styles.statusTrigger} ${isPublished ? styles.statusPublished : styles.statusDraft}`}
                type="button"
                onClick={() => setStatusOpen(open => !open)}
                aria-haspopup="menu"
                aria-expanded={statusOpen}
              >
                <span className={styles.statusDot} />
                <span>{isPublished ? 'Published' : 'Draft'}</span>
                <span className={styles.chevron}><i className="bi bi-chevron-down" /></span>
              </button>
              {statusOpen && (
                <div className={styles.statusMenu} role="menu">
                  <button
                    className={`${styles.statusItem} ${!isPublished ? styles.statusItemActive : ''}`}
                    type="button"
                    onClick={() => handleStatusChange('draft')}
                    role="menuitem"
                  >
                    <span className={styles.itemIcon}>{!isPublished ? <i className="bi bi-check-lg" /> : ''}</span>
                    <span>Draft</span>
                  </button>
                  <button
                    className={`${styles.statusItem} ${isPublished ? styles.statusItemActive : ''}`}
                    type="button"
                    onClick={() => handleStatusChange('published')}
                    role="menuitem"
                  >
                    <span className={styles.itemIcon}>{isPublished ? <i className="bi bi-check-lg" /> : ''}</span>
                    <span>Published</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={`${styles.draftBadge} ${isPublished ? styles.publishedBadge : ''}`}>
              <span>{isPublished ? 'Published' : 'Draft'}</span>
            </div>
          )}
        </div>
        <div className={styles.actions}>
          {isOwner && isPublished && !isEditMode && (
            <button className={styles.iconBtn} onClick={onEdit} aria-label="Edit design" title="Edit design"><i className="bi bi-pencil" /></button>
          )}
          {isOwner && isPublished && isEditMode && (
            <button className={`${styles.iconBtn} ${styles.doneBtn}`} onClick={onDone} aria-label="Done" title="Done"><i className="bi bi-check-lg" /></button>
          )}
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          message={
            confirm === 'publish'
              ? 'Are you sure you want to publish this proposal? It will be visible to customers.'
              : 'Are you sure you want to convert this to Draft? It will be hidden from customers.'
          }
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

export default TopBar;
